import { Link, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { OpenRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { coreCrmClientUrl, dataversePowerAppsClientUrl } from '../../utils/externalClientLinks';

/**
 * The client name, its DBA, and the two links out to where the client is editable.
 *
 * One component for both surfaces (the client record view and the state-tax task view) so
 * the two cannot drift — the DBA rendered one way here and another way there is precisely
 * the sort of inconsistency that makes staff distrust the field.
 *
 * **On the DBA.** `account.cr8f7_companyfictitiousname` has been on the backend's client
 * summary since v1.14.0, where it feeds the upload name-match guard. The v1.14.0 patch notes
 * told staff "a client's fictitious name (DBA) is now shown on the client summary" — but the
 * field was never added to the frontend type, so nothing ever rendered it. This makes the
 * released note true.
 *
 * **On the links.** Field editing in this app is deliberately non-obvious and stays that way;
 * these do not add an edit affordance here, they point at the two systems that own the data.
 * See `utils/externalClientLinks.ts` for why the ptc-core link is keyed on the account GUID
 * and what its failure mode is.
 */

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 },
  nameRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    minWidth: 0,
  },
  // The DBA is an identity fact, not chrome: readable, but clearly subordinate to the legal
  // name so nobody mistakes it FOR the legal name at a glance.
  dba: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  dbaLabel: { color: tokens.colorNeutralForeground3 },
  links: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  link: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    // Header cells / page titles may uppercase or bold their contents; links should not
    // inherit that or they read as headings rather than actions.
    textTransform: 'none',
    letterSpacing: 'normal',
  },
  linkIcon: { fontSize: tokens.fontSizeBase200 },
});

export interface ClientIdentityBlockProps {
  /** The legal / account name, already resolved by the caller. */
  name: string;
  /** `account.cr8f7_companyfictitiousname`. Omitted from the render when absent. */
  fictitiousName?: string | null;
  /** Dataverse `account` GUID — keys the ptc-core CRM link. */
  accountId?: string | null;
  /** `cr8f7_salestaxclientsid` — keys the Dataverse Power Apps link. */
  stateTaxClientId?: string | null;
  /** The caller's own title element, so each page keeps its existing type ramp. */
  children: React.ReactNode;
  className?: string;
}

export default function ClientIdentityBlock({
  name,
  fictitiousName,
  accountId,
  stateTaxClientId,
  children,
  className,
}: ClientIdentityBlockProps) {
  const styles = useStyles();
  const { t } = useTranslation();

  const dba = fictitiousName?.trim();
  // Suppress a DBA that merely restates the legal name — it would read as a second, subtly
  // different name and invite staff to wonder which one the DOR paperwork carries.
  const showDba = !!dba && dba.toLowerCase() !== name.trim().toLowerCase();

  const coreUrl = coreCrmClientUrl(accountId);
  const dataverseUrl = dataversePowerAppsClientUrl(stateTaxClientId);

  return (
    <div className={mergeClasses(styles.root, className)}>
      <div className={styles.nameRow}>
        {children}
        {showDba && (
          <span className={styles.dba}>
            <span className={styles.dbaLabel}>{t('clientIdentity.dbaLabel')}</span>{' '}
            {dba}
          </span>
        )}
      </div>

      {(coreUrl || dataverseUrl) && (
        <div className={styles.links}>
          {coreUrl && (
            <Link
              href={coreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              {t('clientIdentity.openInCore')}
              <OpenRegular className={styles.linkIcon} />
            </Link>
          )}
          {dataverseUrl && (
            <Link
              href={dataverseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              {t('clientIdentity.openInDataverse')}
              <OpenRegular className={styles.linkIcon} />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
